/**
 * ShippingZoneEditor — one row of the shipping zones list.
 *
 * Simple-by-default (name, countries, rate, free-above, days) with an Advanced
 * toggle that reveals:
 *   - Region multi-select (driven by region-catalog.ts)
 *   - Postal-prefix textarea
 *
 * Drag-to-reorder handled by the parent via native HTML5 drag events — this
 * component just renders the drag handle.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import type { ShippingZone } from '@/types';
import {
  getAllCountries,
  getRegionsForCountry,
  getCountryMeta,
  CATALOGS,
} from '@/lib/shipping/region-catalog';

interface Props {
  zone: ShippingZone;
  onChange: (next: ShippingZone) => void;
  onDelete: () => void;

  // Drag state (owned by parent)
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDragEnd: () => void;

  positionLabel: string; // e.g. "1 of 3" — display only
}

function centsToMajor(cents: number): string {
  return (cents / 100).toFixed(2);
}

function majorToCents(value: string): number {
  const parsed = parseFloat(value);
  if (isNaN(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

export function ShippingZoneEditor({
  zone,
  onChange,
  onDelete,
  isDragging,
  onDragStart,
  onDragEnter,
  onDragEnd,
  positionLabel,
}: Props) {
  const [advanced, setAdvanced] = useState(
    () => Boolean(zone.match.regions?.length) || Boolean(zone.match.postalPrefixes?.length)
  );

  const allCountries = useMemo(() => getAllCountries(), []);

  // Countries with region catalogs — show region multi-select only for these
  const catalogedCountriesInZone = useMemo(
    () => zone.match.countries.filter((cc) => Boolean(CATALOGS[cc])),
    [zone.match.countries]
  );

  // Postal prefix preview — human-readable count
  const postalPreview = useMemo(() => {
    const list = zone.match.postalPrefixes ?? [];
    if (list.length === 0) return null;
    if (list.length === 1) return `matches codes starting with "${list[0]}"`;
    if (list.length <= 3) {
      return `matches codes starting with ${list.map((p) => `"${p}"`).join(', ')}`;
    }
    return `matches ${list.length} prefix patterns`;
  }, [zone.match.postalPrefixes]);

  function toggleCountry(cc: string) {
    const upper = cc.toUpperCase();
    const cur = new Set(zone.match.countries);
    if (cur.has(upper)) cur.delete(upper);
    else cur.add(upper);
    // If we dropped a country, prune any of its regions from the regions list
    const newCountries = Array.from(cur).sort();
    const keepRegions = (zone.match.regions ?? []).filter((r) => {
      // A region code is kept if at least one remaining country has it in its catalog
      return newCountries.some((country) => {
        const regions = getRegionsForCountry(country);
        return regions.some((reg) => reg.code === r);
      });
    });
    onChange({
      ...zone,
      match: {
        ...zone.match,
        countries: newCountries,
        regions: keepRegions.length > 0 ? keepRegions : undefined,
      },
    });
  }

  function toggleRegion(code: string) {
    const current = new Set(zone.match.regions ?? []);
    if (current.has(code)) current.delete(code);
    else current.add(code);
    const arr = Array.from(current).sort();
    onChange({
      ...zone,
      match: { ...zone.match, regions: arr.length > 0 ? arr : undefined },
    });
  }

  function updatePostalPrefixes(text: string) {
    const prefixes = text
      .split(/[,\n]/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    onChange({
      ...zone,
      match: { ...zone.match, postalPrefixes: prefixes.length > 0 ? prefixes : undefined },
    });
  }

  function updateField<K extends keyof ShippingZone>(field: K, value: ShippingZone[K]) {
    onChange({ ...zone, [field]: value });
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      className={`bg-card rounded-lg border p-4 transition-opacity ${
        isDragging ? 'opacity-40' : ''
      } ${!zone.enabled ? 'bg-muted/30' : ''}`}
    >
      {/* Row header: drag handle + position + name + enable + delete */}
      <div className="flex items-center gap-3">
        <div
          className="text-muted-foreground cursor-grab active:cursor-grabbing"
          title="Drag to reorder priority"
        >
          <GripVertical className="h-5 w-5" />
        </div>
        <Badge variant="outline" className="shrink-0 font-mono text-xs">
          #{positionLabel}
        </Badge>
        <Input
          className="flex-1 font-medium"
          value={zone.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="Zone name (e.g. Delhi NCR, Europe)"
        />
        <div className="flex items-center gap-2">
          <Label htmlFor={`enabled-${zone.id}`} className="text-muted-foreground text-xs">
            {zone.enabled ? 'Enabled' : 'Disabled'}
          </Label>
          <Switch
            id={`enabled-${zone.id}`}
            checked={zone.enabled}
            onCheckedChange={(v) => updateField('enabled', v)}
          />
        </div>
        <Button variant="ghost" size="icon" onClick={onDelete} title="Delete zone">
          <Trash2 className="text-destructive h-4 w-4" />
        </Button>
      </div>

      <Separator className="my-3" />

      {/* Simple mode fields */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* Countries */}
        <div className="space-y-2 md:col-span-6">
          <Label>Countries</Label>
          <CountryMultiSelect
            value={zone.match.countries}
            onToggle={toggleCountry}
            countries={allCountries}
          />
          {zone.match.countries.length === 0 && (
            <p className="text-destructive text-xs">Select at least one country.</p>
          )}
        </div>

        {/* Rate */}
        <div className="space-y-2 md:col-span-2">
          <Label>Rate</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={centsToMajor(zone.rate)}
            onChange={(e) => updateField('rate', majorToCents(e.target.value))}
            placeholder="0.00"
          />
        </div>

        {/* Free above */}
        <div className="space-y-2 md:col-span-2">
          <Label>Free above</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={zone.freeAbove !== undefined ? centsToMajor(zone.freeAbove) : ''}
            onChange={(e) =>
              updateField(
                'freeAbove',
                e.target.value === '' ? undefined : majorToCents(e.target.value)
              )
            }
            placeholder="None"
          />
        </div>

        {/* Estimated days */}
        <div className="space-y-2 md:col-span-2">
          <Label>Delivery</Label>
          <Input
            value={zone.estimatedDays}
            onChange={(e) => updateField('estimatedDays', e.target.value)}
            placeholder="3-5 days"
          />
        </div>
      </div>

      {/* Advanced toggle */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
        >
          {advanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Advanced: narrow by region or postal code
          {(zone.match.regions?.length || zone.match.postalPrefixes?.length) && !advanced ? (
            <Badge variant="secondary" className="ml-2 text-[10px]">
              active
            </Badge>
          ) : null}
        </button>
      </div>

      {advanced && (
        <div className="mt-3 space-y-4 border-l-2 pl-4">
          {/* Region multi-select (only for cataloged countries in this zone) */}
          {catalogedCountriesInZone.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              Select a country with regional subdivisions (India, US, Canada, etc.) to narrow by
              region.
            </p>
          ) : (
            <div className="space-y-2">
              <Label className="text-sm">Regions</Label>
              <p className="text-muted-foreground text-xs">
                Leave empty to match the entire country. Checked regions narrow the zone.
              </p>
              {catalogedCountriesInZone.map((cc) => (
                <RegionPicker
                  key={cc}
                  country={cc}
                  selected={zone.match.regions ?? []}
                  onToggle={toggleRegion}
                />
              ))}
            </div>
          )}

          {/* Postal prefixes */}
          <div className="space-y-2">
            <Label className="text-sm">Postal code prefixes</Label>
            <p className="text-muted-foreground text-xs">
              Comma or newline-separated. A postal code matches if it <em>starts with</em> any
              prefix. Example: <code>110</code> matches Indian pincodes 110001, 110019, etc.
            </p>
            <Textarea
              rows={2}
              value={(zone.match.postalPrefixes ?? []).join(', ')}
              onChange={(e) => updatePostalPrefixes(e.target.value)}
              placeholder="110, 400, 560"
            />
            {postalPreview && <p className="text-muted-foreground text-xs">→ {postalPreview}</p>}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label className="text-sm">Priority (read-only — reorder by dragging)</Label>
            <Input readOnly value={zone.priority} className="bg-muted max-w-[100px]" />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountryMultiSelect — compact pill-style multi-select with a search input
// ---------------------------------------------------------------------------

function CountryMultiSelect({
  value,
  onToggle,
  countries,
}: {
  value: string[];
  onToggle: (cc: string) => void;
  countries: Array<{ country: string; name: string; flag: string }>;
}) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.country.toLowerCase().includes(q)
    );
  }, [countries, search]);

  return (
    <div className="space-y-2">
      {/* Selected pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((cc) => {
            const meta = getCountryMeta(cc);
            return (
              <Badge
                key={cc}
                variant="secondary"
                className="hover:bg-destructive/20 cursor-pointer"
                onClick={() => onToggle(cc)}
                title="Click to remove"
              >
                {meta?.flag ?? '🌍'} {meta?.name ?? cc} ×
              </Badge>
            );
          })}
        </div>
      )}

      {/* Toggle picker */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-muted-foreground hover:text-foreground text-xs"
      >
        {expanded ? 'Hide' : value.length === 0 ? 'Select countries…' : 'Add more countries'}
      </button>

      {expanded && (
        <div className="bg-background rounded-md border p-2">
          <Input
            placeholder="Search countries"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2 h-8 text-sm"
          />
          <div className="max-h-48 overflow-y-auto">
            {filtered.map((c) => {
              const selected = value.includes(c.country);
              return (
                <button
                  type="button"
                  key={c.country}
                  onClick={() => onToggle(c.country)}
                  className={`hover:bg-accent flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                    selected ? 'bg-accent/50 font-medium' : ''
                  }`}
                >
                  <span>
                    {c.flag} {c.name}{' '}
                    <span className="text-muted-foreground text-xs">({c.country})</span>
                  </span>
                  {selected && <span className="text-xs">✓</span>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-muted-foreground p-2 text-xs">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RegionPicker — grouped checkboxes per cataloged country
// ---------------------------------------------------------------------------

function RegionPicker({
  country,
  selected,
  onToggle,
}: {
  country: string;
  selected: string[];
  onToggle: (code: string) => void;
}) {
  const regions = getRegionsForCountry(country);
  const meta = getCountryMeta(country);
  const [expanded, setExpanded] = useState(false);

  if (regions.length === 0) return null;

  const selectedInCountry = regions.filter((r) => selected.includes(r.code));

  return (
    <div className="bg-background rounded-md border">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="hover:bg-accent flex w-full items-center justify-between px-3 py-2 text-left text-sm"
      >
        <span>
          {meta?.flag} {meta?.name} — {selectedInCountry.length}/{regions.length} selected
        </span>
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {expanded && (
        <div className="grid grid-cols-2 gap-1 border-t p-2 sm:grid-cols-3 md:grid-cols-4">
          {regions.map((r) => (
            <label
              key={r.code}
              className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs"
            >
              <Checkbox
                checked={selected.includes(r.code)}
                onCheckedChange={() => onToggle(r.code)}
              />
              <span className="truncate" title={r.name}>
                {r.name}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
